'use client';

import { createContext, forwardRef, use, useMemo } from 'react';
import { faChevronLeft, faChevronRight, faEllipsis } from '@icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Link from 'next/link';
import { tv } from 'tailwind-variants';

import type { VariantProps } from 'tailwind-variants';

const pagination = tv({
  slots: {
    root: `
      mx-auto
      flex
      w-full
      justify-center
    `,
    content: `
      flex
      flex-row
      items-center
      gap-1
    `,
    item: '',
    link: `
      focus-visible:ring-ring
      hover:bg-background-secondary
      inline-flex
      h-9
      w-9
      items-center
      justify-center
      rounded-md
      p-0
      text-sm
      font-medium
      whitespace-nowrap
      transition-colors
      focus-visible:ring-1
      focus-visible:outline-none
      cursor-pointer
    `,
    ellipsis: `
      flex
      h-9 w-9
      items-center
      justify-center
    `,
    next: `
      w-auto
      gap-1
      px-2.5
      sm:pr-2.5
    `,
    previous: `
      w-auto
      gap-1
      px-2.5
      sm:pl-2.5
    `,
  },
  variants: {
    size: {
      icon: {
        link: `
          h-9 w-9
        `,
      },
      lg: {
        link: `
          h-10 w-10
        `,
      },
      md: {
        link: `
          h-8 w-8
        `,
      },
      sm: {
        link: `
          h-6 w-6
        `,
      },
    },
    disabled: {
      true: {
        link: `
          text-disabled-foreground
          hover:!bg-background
          cursor-default
        `,
      },
    },
    isActive: {
      true: {
        link: `
          border-input
          bg-background
          hover:bg-background-secondary
          border shadow-sm
        `,
      },
      false: {
        link: `
          hover:bg-background-secondary
        `,
      },
    },
  },
  defaultVariants: {
    size: 'icon',
    isActive: false,
  },
});

type PaginationContextProps = {
  size: VariantProps<typeof pagination>['size'];
};

const PaginationContext = createContext<PaginationContextProps | null>(null);

const usePaginationContext = () => {
  const context = use(PaginationContext);
  if (!context) {
    throw new Error('usePaginationContext must be used within a Pagination provider');
  }
  return context;
};

type PaginationProps = React.ComponentProps<'nav'> &
  VariantProps<typeof pagination> & {
    size?: PaginationContextProps['size']; // Allow size override at root
  };

const PaginationRoot = forwardRef<HTMLElement, PaginationProps>(({ className, size = 'icon', ...props }, ref) => {
  const { root } = pagination(); // Get base class for root
  const contextValue = useMemo(() => ({ variant: 'default', size }), [size]);

  return (
    <PaginationContext.Provider value={contextValue}>
      <nav
        className={root({ className })}
        aria-label="pagination"
        data-slot="pagination"
        ref={ref}
        role="navigation"
        {...props}
      />
    </PaginationContext.Provider>
  );
});
PaginationRoot.displayName = 'Pagination';

const PaginationContent = forwardRef<HTMLUListElement, React.ComponentProps<'ul'>>(({ className, ...props }, ref) => {
  const { content } = pagination();
  return <ul className={content({ className })} data-slot="pagination-content" ref={ref} {...props} />;
});
PaginationContent.displayName = 'PaginationContent';

const PaginationItem = forwardRef<HTMLLIElement, React.ComponentProps<'li'>>(({ className, ...props }, ref) => {
  const { item } = pagination();
  return <li className={item({ className })} data-slot="pagination-item" ref={ref} {...props} />;
});
PaginationItem.displayName = 'PaginationItem';

type PaginationLinkProps = Omit<React.ComponentProps<typeof Link>, 'href'> &
  Pick<VariantProps<typeof pagination>, 'isActive' | 'size'> & {
    disabled?: boolean;
    href?: string;
  };

const PaginationLink = forwardRef<HTMLAnchorElement | HTMLButtonElement, PaginationLinkProps>(
  ({ className, isActive, size: localSize, disabled, ...props }, ref) => {
    const { size: contextSize } = usePaginationContext();
    const finalSize = localSize ?? contextSize; // Prefer local prop size
    const { link } = pagination({ size: finalSize, isActive, disabled });

    if (props.href) {
      const { href, ...rest } = props as React.ComponentProps<typeof Link>;
      return (
        <Link
          className={link({ className })}
          aria-current={isActive ? 'page' : undefined}
          data-active={isActive ? 'true' : undefined}
          data-slot="pagination-link"
          href={href}
          ref={ref as React.ForwardedRef<HTMLAnchorElement>}
          {...rest}
        />
      );
    }

    return (
      <button
        className={link({ className })}
        aria-current={isActive ? 'page' : undefined}
        data-active={isActive ? 'true' : undefined}
        data-slot="pagination-link"
        ref={ref as React.ForwardedRef<HTMLButtonElement>}
        {...(props as React.ComponentProps<'button'>)}
      />
    );
  }
);
PaginationLink.displayName = 'PaginationLink';

const PaginationPrevious = forwardRef<HTMLAnchorElement | HTMLButtonElement, Omit<PaginationLinkProps, 'isActive'>>(
  ({ className, ...props }, ref) => {
    const { previous } = pagination();
    return (
      <PaginationLink
        className={previous({ className })}
        aria-label="Go to previous page"
        ref={ref}
        size="lg"
        {...props}
      >
        <FontAwesomeIcon icon={faChevronLeft} />
        <span>Previous</span>
      </PaginationLink>
    );
  }
);
PaginationPrevious.displayName = 'PaginationPrevious';

const PaginationNext = forwardRef<HTMLAnchorElement | HTMLButtonElement, Omit<PaginationLinkProps, 'isActive'>>(
  ({ className, ...props }, ref) => {
    const { next } = pagination();
    return (
      <PaginationLink className={next({ className })} aria-label="Go to next page" ref={ref} size="lg" {...props}>
        <span>Next</span>
        <FontAwesomeIcon icon={faChevronRight} />
      </PaginationLink>
    );
  }
);
PaginationNext.displayName = 'PaginationNext';

const PaginationEllipsis = forwardRef<HTMLSpanElement, React.ComponentProps<'span'>>(({ className, ...props }, ref) => {
  const { ellipsis } = pagination();
  return (
    <span className={ellipsis({ className })} aria-hidden data-slot="pagination-ellipsis" ref={ref} {...props}>
      <FontAwesomeIcon icon={faEllipsis} />
      <span className="sr-only">More pages</span>
    </span>
  );
});
PaginationEllipsis.displayName = 'PaginationEllipsis';

// Renamed exports to match the convention (Root as main export)
export {
  pagination, // Export the tv function
  PaginationRoot as Pagination, // Export the root component as default
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
  type PaginationProps, // Export the main props type
};
